import { Mobile } from '../shared/components/input/contact/mobile/mobile.interface';
import { Telephone } from '../shared/components/input/contact/telephone/telephone.interface';
import { ICategory } from './category';
import { CurrencyCode } from './enum/currency-code';
import { IProduct } from './product';
import { OpeningHours } from './opening-hours';


export interface IAddress {
  email: string;
  zipCode: string;
  city: string;
  street: string;
}

export type IStoreAddress = IAddress & { 
  unitFloorRoomBuildingNo: string;
  buildingNameTower: string;
  lotBlockPhaseHouseNo: string;
  subdivisionVillageZone: string;
  barangay: string;
  district: string;
  province: string;
};

export interface StoreCreateData extends IStoreAddress {
  name: string;
  email: string;
  mobile: Mobile;
  telephone?: Telephone;
  website?: string;
  about: string;
  currency: CurrencyCode;
  taxId: string;
  branchCode: string;
  permitToUseNumber: string;
  authorityToGenerateNumber: string;
  openingHours?: OpeningHours;
  isOpen: boolean;
}

export interface IStore extends StoreCreateData {
  id: number;
  createdAt: Date;
  updatedAt: Date;
  openingHours: OpeningHours;
  categoryCount: number;
  productCount: number;
  itemImagesCount: number;
  itemDescriptionCount: number;
  categories: ICategory[];
}

export class Store implements IStore {
  constructor(
    public readonly id: number = 0,
    public readonly createdAt: Date = new Date(),
    public readonly updatedAt: Date = new Date(),
    public readonly name: string = '',
    public readonly email: string = '',
    public readonly mobile: Mobile = { countryCode: '', number: '' },
    public readonly about: string = '',
    public readonly currency: CurrencyCode = CurrencyCode.PHP,
    public readonly taxId: string = '',
    public readonly branchCode: string = '',
    public readonly permitToUseNumber: string = '',
    public readonly authorityToGenerateNumber: string = '',
    public readonly unitFloorRoomBuildingNo: string = '',
    public readonly buildingNameTower: string = '',
    public readonly lotBlockPhaseHouseNo: string = '',
    public readonly street: string = '',
    public readonly subdivisionVillageZone: string = '',
    public readonly barangay: string = '',
    public readonly district: string = '',
    public readonly city: string = '',
    public readonly province: string = '',
    public readonly zipCode: string = '',
    public readonly openingHours: OpeningHours = {} as OpeningHours,
    public readonly categoryCount: number = 0,
    public readonly productCount: number = 0,
    public readonly itemImagesCount: number = 0,
    public readonly itemDescriptionCount: number = 0,
    public readonly categories: ICategory[] = [],
    public readonly isOpen: boolean = false, // TODO: Check if needed
    public readonly telephone?: Telephone,
    public readonly website?: string,
  ) {}

  // TODO: Remove. Don't imply from store name. Assume API provides this.
  get slug() {
    return this.name.replace(/ /g, '-').toLowerCase();
  }

  get address() {
    const addressParts = [];
    if (this.unitFloorRoomBuildingNo) addressParts.push(this.unitFloorRoomBuildingNo);
    if (this.buildingNameTower) addressParts.push(this.buildingNameTower);
    if (this.lotBlockPhaseHouseNo) addressParts.push(this.lotBlockPhaseHouseNo);
    if (this.street) addressParts.push(this.street);
    if (this.subdivisionVillageZone) addressParts.push(this.subdivisionVillageZone);
    if (this.barangay) addressParts.push(this.barangay);
    if (this.district) addressParts.push(this.district);
    if (this.city) addressParts.push(this.city);
    if (this.province) addressParts.push(this.province);
    if (this.zipCode) addressParts.push(this.zipCode);
    return addressParts.length > 0 ? addressParts.join(', ') : null;
  }

  static fromJSON(json: IStore) {
    return new Store(
      json.id,
      json.createdAt,
      json.updatedAt,
      json.name,
      json.email,
      json.mobile,
      json.about,
      json.currency,
      json.taxId,
      json.branchCode,
      json.permitToUseNumber,
      json.authorityToGenerateNumber,
      json.unitFloorRoomBuildingNo,
      json.buildingNameTower,
      json.lotBlockPhaseHouseNo,
      json.street,
      json.subdivisionVillageZone,
      json.barangay,
      json.district,
      json.city,
      json.province,
      json.zipCode,
      json.openingHours,
      json.categoryCount,
      json.productCount,
      json.itemImagesCount,
      json.itemDescriptionCount,
      json.categories,
      json.isOpen, // TODO: Check if needed
      json.telephone,
      json.website,
    );
  }
}

// Interfaces used for stores/<slug>/shop page

export interface ICartItem {
  product: IProduct;
  quantity: number;
}

export interface IOrderSummary {
  items: ICartItem[];
  totalAmount: number;
}

export interface ICheckoutDetails {
  shippingAddress: IAddress;
  paymentMethod: string;
  items: ICartItem[];
}

export interface IOrderStatus {
  orderId: number;
  status: string; // e.g., 'Pending', 'Shipped', 'Delivered'
  estimatedDelivery: Date;
}
